import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Minus, Plus, ShoppingBag, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCart } from '@/contexts/CartContext';
import { toast } from 'sonner';

const Cart = () => {
  const { cart, updateQuantity, removeFromCart, loading } = useCart();
  const navigate = useNavigate();

  const handleQuantityChange = async (itemId, newQuantity) => {
    try {
      await updateQuantity(itemId, newQuantity);
    } catch (error) {
      toast.error('Erro ao atualizar quantidade');
    }
  };

  const handleRemove = async (itemId) => {
    try {
      await removeFromCart(itemId);
      toast.success('Item removido do carrinho');
    } catch (error) {
      toast.error('Erro ao remover item');
    }
  };

  if (cart.items.length === 0) {
    return (
      <div className="min-h-screen pt-24" data-testid="cart-page">
        <div className="container-luxury py-12">
          <div className="empty-state">
            <ShoppingBag className="w-16 h-16 text-muted-foreground mb-6" />
            <h1 className="font-serif text-3xl mb-4">Seu Carrinho está Vazio</h1>
            <p className="text-muted-foreground mb-8">
              Explore nossa coleção e encontre peças que você vai amar
            </p>
            <Link to="/shop">
              <Button className="btn-primary" data-testid="continue-shopping-btn">
                Continuar Comprando
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24" data-testid="cart-page">
      <div className="container-luxury py-12">
        <h1 className="font-serif text-4xl mb-12">Carrinho de Compras</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-6">
            {cart.items.map((item) => (
              <div 
                key={item.id} 
                className="flex gap-6 p-6 border border-white/10 bg-card/30"
                data-testid={`cart-item-${item.id}`}
              >
                {/* Image */}
                <div className="w-24 h-32 flex-shrink-0 bg-card overflow-hidden">
                  <img
                    src={item.product_image}
                    alt={item.product_name}
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Details */}
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-medium mb-1">{item.product_name}</h3>
                      <p className="text-sm text-muted-foreground">
                        Tamanho: {item.size} | Cor: {item.color}
                      </p>
                    </div>
                    <button
                      onClick={() => handleRemove(item.id)}
                      className="p-2 text-muted-foreground hover:text-red-500 transition-colors"
                      data-testid={`remove-item-${item.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="flex justify-between items-end mt-4">
                    {/* Quantity */}
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                        disabled={loading || item.quantity <= 1}
                        className="qty-btn"
                        data-testid={`qty-decrease-${item.id}`}
                      >
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="w-8 text-center">{item.quantity}</span>
                      <button
                        onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                        disabled={loading}
                        className="qty-btn"
                        data-testid={`qty-increase-${item.id}`}
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                    </div>

                    {/* Price */}
                    <p className="text-primary font-bold">
                      ${(item.price * item.quantity).toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="p-8 bg-card border border-white/10 sticky top-28">
              <h2 className="font-serif text-2xl mb-6">Resumo do Pedido</h2>
              
              <div className="space-y-4 mb-6">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal ({cart.item_count} itens)</span>
                  <span>${cart.total.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Frete</span>
                  <span className="text-green-500">Grátis</span>
                </div>
              </div>

              <div className="border-t border-white/10 pt-4 mb-8">
                <div className="flex justify-between text-lg font-bold">
                  <span>Total</span>
                  <span className="text-primary" data-testid="cart-total">
                    ${cart.total.toFixed(2)}
                  </span>
                </div>
              </div>

              <Button
                onClick={() => navigate('/checkout')}
                className="btn-primary w-full"
                data-testid="checkout-btn"
              >
                Finalizar Compra
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>

              <Link to="/shop">
                <Button variant="ghost" className="btn-ghost w-full mt-4">
                  Continuar Comprando
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
